import {inject, Injectable} from "@angular/core";
import {map, Observable} from "rxjs";
import {FundValuation} from "../models/fund-valuation";
import {
    FundApiService,
    FundWeightedCashflowBreakdown,
    GoalSeekRequest,
    ReferenceNTvpi,
    ScenarioNTvpi,
    PreviewReturnSummaryRequest,
    UpdateFundValuationDescriptionRequest,
    UpdateFundValuationManagementFeeScaleRequest
} from "../generated-sources/api";
import {CashflowPattern} from "../../shared/model/pattern/cashflow-pattern";
import {CashflowPatternUpdateResult} from "../models/cashflow-pattern-update-result";
import {FundValuationScenario} from "../models/fund-valuation-scenario";
import {FundValuationWithScenarios} from "../models/fund-valuation-with-scenarios";
import {AggregatedFundCashflows} from "../models/aggregated-fund-cashflows";
import {TraceableText} from "../../shared/model/traceable";
import {FundValuationOverviewPage} from "../models/fund-valuation-overview-page";
import {FundValuationOverviewParams} from "../store/fund.state";
import {CopyConfig} from "../models/copy-config";
import {AssetValuationsSelectionRow} from "../models/asset-valuation-selection-row";
import {PatternAssignment} from "../models/pattern-assignment";
import {TopDownValuationCalcRequest} from "../models/top-down-valuation-calc-request";
import {FundValuationWithScnsAndAggrCFs} from "../models/fund-valuation-with-scenarios-and-aggr-cfs";
import {FundValuationDeferralData} from "../models/fund-valuation-deferral-data";
import {FundData} from "../models/fund-data";

@Injectable({
    providedIn: "root"
})
export class FundValuationService {
    private readonly fundApiService = inject(FundApiService);

    constructor() {
    }

    public searchFundValuationsOverview(params: FundValuationOverviewParams): Observable<FundValuationOverviewPage> {
        return this.fundApiService.searchFundValuationsOverview(
            params.search || undefined,
            params.page,
            params.size,
            params.sort
        );
    }

    public getValuationsForFundReport(fundId: number, fundReportID: number): Observable<FundValuation[]> {
        return this.fundApiService.getValuationsForFundReport(fundId, fundReportID);
    }

    public getValuationsForFund(fundId: number): Observable<FundValuation[]> {
        return this.fundApiService.getValuationsForFund(fundId); // Should only be used for the DealFundsManagement
    }

    public getFundValuation(fundId: number, fundReportId: number, fundValuationId: number): Observable<FundValuation> {
        return this.fundApiService.getFundValuation(fundId, fundReportId, fundValuationId);
    }

    public createFundValuation(fundId: number, fundReportId: number, fundValuation: FundValuation, templateId?: number): Observable<FundValuationWithScenarios> {
        return this.fundApiService.createFundValuation(fundId, fundReportId, fundValuation, templateId);
    }

    public updateFundValuation(
        fundId: number,
        fundReportId: number,
        fundValuationId: number,
        fundValuation: FundValuation,
        scenarios?: FundValuationScenario[],
        dealId?: number,
        dataset?: string
    ): Observable<FundData> {
        return this.fundApiService.updateFundValuation(fundId, fundReportId, fundValuationId, {
            fundValuationWithScenarios: {fundValuation, scenarios: scenarios ?? []},
            dealId,
            dataset
        });
    }

    public deleteFundValuation(fundId: number, fundReportId: number, fundValuationId: number): Observable<void> {
        return this.fundApiService.deleteFundValuation(fundId, fundReportId, fundValuationId);
    }

    public getScenariosForFundValuation(fundId: number, fundValuationId: number): Observable<FundValuationScenario[]> {
        return this.fundApiService.getScenariosForFundValuation(fundId, fundValuationId);
    }

    /** Most-recent prior forecast nTVPI per scenario (+ its source valuation) — the reference column for the review step. */
    public getReferenceNTvpi(fundId: number, fundValuationId: number): Observable<ReferenceNTvpi> {
        return this.fundApiService.getReferenceNTvpi(fundId, fundValuationId);
    }

    /** Goal-seek each scenario's NAV multiple to the supplied target nTVPIs, persist + recalc; returns updated scenarios. */
    public goalSeekNavMultiplesToTargets(fundId: number, fundReportId: number, fundValuationId: number, targets: ScenarioNTvpi): Observable<FundValuationScenario[]> {
        return this.fundApiService.goalSeekNavMultiplesToTargets(fundId, fundReportId, fundValuationId, targets);
    }

    public getFundValuationScenario(fundId: number, fundValuationId: number, scenarioId: number): Observable<FundValuationScenario> {
        return this.fundApiService.getFundValuationScenario(fundId, fundValuationId, scenarioId);
    }

    public saveScenario(fundId: number, fundValuationId: number, scenario: FundValuationScenario): Observable<FundValuationScenario> {
        const result: Observable<FundValuationScenario> = !!scenario.id && !!fundId && !!fundValuationId
            ? this.fundApiService.updateFundValuationScenario(fundId, fundValuationId, scenario.id, scenario)
            : this.fundApiService.createFundValuationScenario(fundId, fundValuationId, scenario);
        return result.pipe(map((scenarioDto: FundValuationScenario) => scenarioDto));
    }

    public saveScenariosForFundValuation(fundId: number, fundValuationId: number, scenarios: FundValuationScenario[]): Observable<FundValuationScenario[]> {
        return this.fundApiService.updateScenariosForFundValuation(fundId, fundValuationId, scenarios);
    }

    public getAggregatedFundCashflows(fundId: number, fundValuationId: number, scenarioId: number): Observable<AggregatedFundCashflows> {
        return this.fundApiService.getAggregatedFundCashflows(fundId, fundValuationId, scenarioId);
    }

    public calculateAggregatedFundCashflows(fundId: number, fundValuationId: number, scenarioId: number): Observable<AggregatedFundCashflows> {
        return this.fundApiService.calculateAggregatedFundCashflows(fundId, fundValuationId, scenarioId);
    }

    public getWeightedCashflowBreakdown(fundValuationId: number, date: string, cashflowTypes: string[]): Observable<FundWeightedCashflowBreakdown> {
        return this.fundApiService.getFundWeightedCashflowBreakdown(fundValuationId, date, cashflowTypes);
    }

    public calcTopDownValuation(fundId: number, fundValuationId: number, request: TopDownValuationCalcRequest): Observable<FundValuationWithScenarios> {
        return this.fundApiService.calculateTopDownValuation(fundId, fundValuationId, request);
    }

    public calcUndrawnValuation(fundId: number, fundValuationId: number, undrawnValuation: FundValuationWithScenarios): Observable<FundValuationWithScenarios> {
        return this.fundApiService.calculateUndrawnValuation(fundId, fundValuationId, undrawnValuation);
    }

    public applyUnfundedParameters(fundId: number, fundValuationId: number, undrawnValuation: FundValuationWithScenarios): Observable<FundValuationWithScenarios> {
        return this.fundApiService.applyUnfundedParameters(fundId, fundValuationId, undrawnValuation);
    }

    public calcReturnSummary(fundId: number, fundValuationId: number): Observable<FundValuationWithScenarios> {
        return this.fundApiService.calculateFundReturnSummary(fundId, fundValuationId);
    }

    public previewReturnSummary(fundId: number, fundValuationId: number, request: PreviewReturnSummaryRequest): Observable<FundValuationWithScenarios> {
        return this.fundApiService.previewReturnSummary(fundId, fundValuationId, request);
    }

    public goalSeek(fundId: number, fundValuationId: number, request: GoalSeekRequest): Observable<FundValuationWithScenarios> {
        return this.fundApiService.goalSeek(fundId, fundValuationId, request);
    }

    public getAssetValuationsForCopyWorkflow(fundId: number, fundReportId: number, fundValuationId: number, copyConfig: CopyConfig): Observable<AssetValuationsSelectionRow[]> {
        return this.fundApiService.getAssetValuationsForCopyWorkflow(fundId, fundReportId, fundValuationId, copyConfig);
    }

    public assignPatterns(fundId: number, fundReportId: number, fundValuationId: number, patternAssignment: PatternAssignment): Observable<FundValuationWithScnsAndAggrCFs> {
        return this.fundApiService.assignPatterns(fundId, fundReportId, fundValuationId, patternAssignment);
    }

    public applyTemplateGroup(fundId: number, fundReportId: number, fundValuationId: number, templateId: number): Observable<FundValuationWithScnsAndAggrCFs> {
        return this.fundApiService.applyTemplateGroup(fundId, fundReportId, fundValuationId, templateId);
    }

    public updatePatternForFundValuation(
        fundId: number, fundReportId: number, fundValuationId: number, patternId: number, pattern: CashflowPattern
    ): Observable<CashflowPatternUpdateResult> {
        return this.fundApiService.updatePatternForFundValuation(fundId, fundReportId, fundValuationId, patternId, pattern);
    }

    public updateFundValuationScenariosNca(fundId: number,
                                           fundReportId: number,
                                           fundValuationId: number,
                                           netCurrentAssetsDate: string,
                                           lowNca: number,
                                           baseNca: number,
                                           highNca: number,
                                           dealId?: number,
                                           dataset?: string
    ) {
        return this.fundApiService.updateFundValuationScenariosNca(fundId, fundReportId, fundValuationId, {
            netCurrentAssetsDate,
            lowNca,
            baseNca,
            highNca,
            dealId,
            dataset
        });
    }

    public updateFundValuationDescription(fundValuationId: number, description: UpdateFundValuationDescriptionRequest): Observable<TraceableText> {
        return this.fundApiService.updateFundValuationDescription(fundValuationId, description);
    }

    public updateFundValuationManagementFeeScale(
        fundId: number,
        fundReportId: number,
        fundValuationId: number,
        managementFeeScale: UpdateFundValuationManagementFeeScaleRequest
    ): Observable<FundValuationWithScnsAndAggrCFs> {
        return this.fundApiService.updateFundValuationManagementFeeScale(fundId, fundReportId, fundValuationId, managementFeeScale);
    }

    public getDeferralsForFundValuation(
        fundId: number,
        fundValuationId: number
    ): Observable<FundValuationDeferralData> {
        return this.fundApiService.getDeferralsForFundValuation(fundId, fundValuationId)
    }

    public upsertFundValuationDeferrals(
        fundId: number,
        fundValuationId: number,
        fundValuationDeferralData: FundValuationDeferralData
    ): Observable<FundValuationDeferralData> {
        return this.fundApiService.upsertFundValuationDeferrals(fundId, fundValuationId, fundValuationDeferralData)
    }
}
